/**
 * ExportButtons Component
 *
 * Provides PDF and CSV export functionality for listing pages.
 * PDF export navigates to a print preview page.
 * CSV export downloads directly.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  Download,
  PictureAsPdf,
  TableChart,
  KeyboardArrowDown,
} from '@mui/icons-material'
import Button from './Button'
import { downloadCSV } from '@utils/exportUtils'
import { toast } from 'react-toastify'

const ExportButtons = ({
  data = [],
  columns = [],
  title = 'Export',
  filename = 'export',
  filters = {},
  subtitle = '',
  backPath,
  disabled = false,
  // Option to use a menu or separate buttons
  variant = 'menu', // 'menu' | 'buttons'
}) => {
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Pre-process data to apply renderValue functions and make it serializable
  const processDataForExport = () => {
    // Filter out action columns and get exportable columns
    const exportColumns = columns.filter(col => col.field !== 'actions' && !col.noExport)

    // Create serializable column definitions (only field and headerName)
    const serializableColumns = exportColumns.map(col => ({
      field: col.field,
      headerName: col.headerName || col.field,
      align: col.align,
    }))

    // Pre-process data - apply renderValue/exportValue functions
    const processedData = data.map(row => {
      const processedRow = { ...row }
      exportColumns.forEach(col => {
        const value = row[col.field]
        if (col.renderValue) {
          processedRow[`__export_${col.field}`] = col.renderValue(value, row)
        } else if (col.exportValue) {
          processedRow[`__export_${col.field}`] = col.exportValue(value, row)
        }
      })
      return processedRow
    })

    return { processedData, serializableColumns }
  }

  const handleExportPDF = () => {
    handleClose()
    if (!data || data.length === 0) {
      toast.warning('No data to export')
      return
    }

    const { processedData, serializableColumns } = processDataForExport()

    // Navigate to print preview page with pre-processed data
    navigate('/export/preview', {
      state: {
        title,
        data: processedData,
        columns: serializableColumns,
        filters,
        subtitle,
        backPath: backPath || window.location.pathname,
      }
    })
  }

  const handleExportCSV = () => {
    handleClose()
    if (!data || data.length === 0) {
      toast.warning('No data to export')
      return
    }

    const success = downloadCSV(data, columns, filename)
    if (success) {
      toast.success('CSV exported successfully')
    } else {
      toast.error('Failed to export CSV')
    }
  }

  if (variant === 'buttons') {
    return (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdf />}
          onClick={handleExportPDF}
          disabled={disabled || !data?.length}
          size="small"
        >
          PDF
        </Button>
        <Button
          variant="outlined"
          startIcon={<TableChart />}
          onClick={handleExportCSV}
          disabled={disabled || !data?.length}
          size="small"
        >
          CSV
        </Button>
      </Box>
    )
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<Download />}
        endIcon={<KeyboardArrowDown />}
        onClick={handleClick}
        disabled={disabled || !data?.length}
      >
        Export
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleExportPDF}>
          <ListItemIcon>
            <PictureAsPdf fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as PDF</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleExportCSV}>
          <ListItemIcon>
            <TableChart fontSize="small" />
          </ListItemIcon>
          <ListItemText>Export as CSV</ListItemText>
        </MenuItem>
      </Menu>
    </>
  )
}

export default ExportButtons
